import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getSessionAnalytics } from '../../services/api';
import { useApiQuery } from '../../hooks/useApiQuery';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';


const Devices = () => {
  const { eventId } = useOutletContext();


  // =====================================================
  // Session Analytics
  // =====================================================

  const {
    data: analyticsData = {},
    isLoading,
    isError,
  } = useApiQuery(
    [
      'session-analytics',
      eventId,
    ],
    () => getSessionAnalytics(eventId),
    {
      enabled:
        Boolean(eventId),

      staleTime:
        60 * 1000,

      onError: error => {
        console.error(
          'Failed to fetch device analytics:',
          error
        );
      },
    }
  );


  // =====================================================
  // Device usage
  // =====================================================

  const deviceUsage = useMemo(() => {

    const deviceAnalytics =
      Array.isArray(
        analyticsData?.deviceAnalytics
      )
        ? analyticsData.deviceAnalytics
        : [];


    return deviceAnalytics
      .map(device => ({
        name:
          device.name ||
          device.deviceName ||
          device.deviceId ||
          'Unknown Device',

        scans:
          Number(
            device.scans ??
            device.count ??
            device.totalScans
          ) || 0,
      }))
      .sort(
        (a, b) =>
          b.scans - a.scans
      );

  }, [analyticsData]);


  // =====================================================
  // Statistics
  // =====================================================

  const stats = useMemo(() => {

    const totalScans =
      deviceUsage.reduce(
        (sum, device) =>
          sum + device.scans,
        0
      );


    const activeDevices =
      deviceUsage.filter(
        device =>
          device.scans > 0
      ).length;


    const topDevice =
      deviceUsage[0]?.name ||
      '—';


    const topScans =
      deviceUsage[0]?.scans ||
      0;


    return {
      totalScans,
      activeDevices,
      topDevice,
      topScans,
    };

  }, [deviceUsage]);


  // =====================================================
  // Loading
  // =====================================================

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">

        <div className="text-center">

          <div
            className="
              inline-block
              w-8
              h-8
              border-4
              border-[#1471b9]
              border-t-transparent
              rounded-full
              animate-spin
              mb-3
            "
          />

          <p className="text-[#708090] text-sm font-['Barlow']">
            Loading device analytics...
          </p>

        </div>

      </div>
    );
  }


  // =====================================================
  // Error
  // =====================================================

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16">

        <div className="text-center">

          <p className="text-red-500 text-sm">
            Failed to load device analytics
          </p>

          <p className="text-[#708090] text-xs mt-2">
            Please refresh the page or try again later.
          </p>

        </div>

      </div>
    );
  }


  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="space-y-6 font-['Barlow']">

      {/* Page Header */}

      <div>

        <h1 className="text-2xl font-normal leading-9 text-[#2b3445]">
          Device Analytics
        </h1>

        <p className="text-sm text-[#708090] mt-1">
          Scan performance per device for the selected event
        </p>

      </div>


      {/* Summary Cards */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">

          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">
            Total Scans
          </p>

          <p className="text-2xl font-bold text-[#2b3445] mt-1">
            {stats.totalScans}
          </p>

        </div>


        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">

          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">
            Active Devices
          </p>

          <p className="text-2xl font-bold text-[#2b3445] mt-1">
            {stats.activeDevices}
          </p>

        </div>


        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5">

          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">
            Top Device
          </p>

          <p className="text-lg font-semibold text-[#2b3445] truncate mt-1">
            {stats.topDevice}
          </p>

          <p className="text-sm text-[#1471b9]">
            {stats.topScans} scans
          </p>

        </div>

      </div>


      {/* Chart */}

      <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-5 md:p-6">

        <div className="flex items-center justify-between mb-5">

          <h2 className="text-[17.6px] font-semibold leading-[26.4px] text-[#2b3445]">

            <span className="inline-block w-1.5 h-5 bg-[#1471b9] rounded-full mr-2 align-middle" />

            Scans per Device

          </h2>


          {deviceUsage.length === 0 && (
            <span className="text-xs text-[#708090]">
              No data available
            </span>
          )}

        </div>


        {deviceUsage.length === 0 ? (

          <div className="flex flex-col items-center justify-center py-12 text-[#708090]">

            <svg
              className="w-12 h-12 mb-3 text-[#dae1e7]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>

            <p className="text-sm">
              No scan activity recorded for this event
            </p>

            <p className="text-xs mt-1">
              Check back after attendees check in
            </p>

          </div>

        ) : (

          <ResponsiveContainer
            width="100%"
            height={400}
          >

            <BarChart
              data={deviceUsage}
              margin={{
                top: 20,
                right: 20,
                left: 0,
                bottom: 20,
              }}
            >

              <CartesianGrid
                stroke="#dae1e7"
                strokeDasharray="3 3"
                vertical={false}
              />

              <XAxis
                dataKey="name"
                tick={{
                  fill: '#708090',
                  fontSize: 12,
                  fontFamily:
                    'Barlow, sans-serif',
                }}
                axisLine={{
                  stroke:
                    '#dae1e7',
                }}
                tickLine={false}
              />

              <YAxis
                tick={{
                  fill: '#708090',
                  fontSize: 12,
                  fontFamily:
                    'Barlow, sans-serif',
                }}
                axisLine={{
                  stroke:
                    '#dae1e7',
                }}
                tickLine={false}
                allowDecimals={false}
              />

              <Tooltip
                cursor={{
                  fill: '#f6f9fc',
                }}
                contentStyle={{
                  backgroundColor:
                    '#ffffff',
                  border:
                    '1px solid #dae1e7',
                  borderRadius:
                    '8px',
                  boxShadow:
                    '0px 4px 16px 0px rgba(43,52,69,0.1)',
                  fontSize: '12px',
                  fontFamily:
                    'Barlow, sans-serif',
                  color:
                    '#2b3445',
                }}
                labelStyle={{
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              />

              <Bar
                dataKey="scans"
                fill="#1471b9"
                radius={[
                  4,
                  4,
                  0,
                  0,
                ]}
                barSize={48}
                animationDuration={500}
              />

            </BarChart>

          </ResponsiveContainer>

        )}

      </div>

    </div>
  );
};


export default Devices;